import React, { useEffect, useState } from 'react';
import { SalesInvoiceDataForm } from '../../../../app/models/SalesInvoice';
import useHandleErrorFields from '../../../../hooks/useHandleErrorFields';
import SelectTwo, { SelectTwoMapperOptions } from '../../../../components/SelectTwo';
import ButtonTableAction from '../../../../components/buttons/ButtonTableAction';
import LazyLoading from '../../../../components/LazyLoading';

interface Props {
    salesInvoice: SalesInvoiceDataForm;
    setSalesInvoice: (salesInvoice: SalesInvoiceDataForm) => void;
    is_edit?: boolean;
    errorFields?: any;
    societies?: any[];
    clients?: any[];
    deliveries?: any[];
    onSocietyChange?: (societyId: number | string) => void;
    onClientChange?: (clientId: number | string) => void;
    loadingClients?: boolean;
    loadingDeliveries?: boolean;
}

const SalesInvoiceForm = ({
    salesInvoice,
    setSalesInvoice,
    is_edit = false,
    errorFields,
    societies = [],
    clients = [],
    deliveries = [],
    onSocietyChange,
    onClientChange,
    loadingClients = false,
    loadingDeliveries = false
}: Props) => {
    const { fieldErrorMessage, fieldHasError, onFocusRemove, setErrors } = useHandleErrorFields();

    const [selectedDelivery, setSelectedDelivery] = useState<any>(null);
    const [addedDeliveries, setAddedDeliveries] = useState<any[]>([]);

    // Sincronizar errores del backend
    useEffect(() => {
        setErrors(errorFields);
    }, [errorFields, setErrors]);

    const handleChange = (
        e: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLSelectElement>
    ) => {
        const { name, value } = e.target;
        setSalesInvoice({ ...salesInvoice, [name]: value });
    };

    // Mapear opciones para SelectTwo
    const societyOptions = SelectTwoMapperOptions(societies, 'id', ['name']);
    const clientOptions = SelectTwoMapperOptions(clients, 'id', ['name']);
    
    // Mapear deliveries para SelectTwo: solo ID de entrega e ID de pedido
    const deliveryOptions = deliveries
        .filter((delivery: any) => !addedDeliveries.some(d => d.id === delivery.id))
        .map((delivery: any) => ({
            id: delivery.id,
            name: `Entrega #${delivery.id} - Pedido #${delivery.sales_order_id ?? '-'}`,
            sales_order_id: delivery.sales_order_id,
            net_amount: delivery.net_amount || 0
        }));

    const addDelivery = () => {
        if (selectedDelivery && !addedDeliveries.some(d => d.id === selectedDelivery.id)) {
            setAddedDeliveries([...addedDeliveries, selectedDelivery]);
            setSelectedDelivery(null); // Limpiar el select después de agregar
        }
    };

    const removeDelivery = (id: number) => {
        const updatedDeliveries = addedDeliveries.filter(d => d.id !== id);
        setAddedDeliveries(updatedDeliveries);
    };

    const formatCurrency = (amount: number) => {
        return new Intl.NumberFormat('es-CL', {
            style: 'currency',
            currency: 'CLP',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }).format(amount);
    };

    // Calcular total
    const totalAmount = addedDeliveries.reduce((sum, delivery) => {
        return sum + (delivery.net_amount || 0);
    }, 0);

    // Actualizar salesInvoice cuando cambien las entregas agregadas
    useEffect(() => {
        const deliveryIds = addedDeliveries.map(d => d.id);
        setSalesInvoice({
            ...salesInvoice,
            payment_statuses_id: deliveryIds
        });
    }, [addedDeliveries]);

    return (
        <div className="sales-invoice-form">
            {/* Sociedad */}
            <div className="mb-2 row">
                <label className="col-md-3 col-form-label">Sociedad</label>
                <div className="col-md-9">
                    <SelectTwo
                        name="society_id"
                        id="society_id"
                        inputValue={salesInvoice.society_id}
                        options={societyOptions}
                        hasError={fieldHasError('society_id') !== ''}
                        onChange={(option) => {
                            setSalesInvoice({ 
                                ...salesInvoice, 
                                society_id: option?.value || '',
                                client_id: '', // Reset client when society changes
                                payment_statuses_id: [] // Reset deliveries
                            });
                            onFocusRemove('society_id');
                            if (onSocietyChange && option?.value) {
                                onSocietyChange(option.value);
                            }
                            // Limpiar entregas cuando cambie la sociedad
                            setAddedDeliveries([]);
                            setSelectedDelivery(null);
                        }}
                        placeholder="Seleccione una sociedad"
                        onFocus={() => onFocusRemove('society_id')}
                        disabled={is_edit}
                    />
                    <div className="invalid-feedback" style={{ display: 'flex' }}>
                        {fieldErrorMessage('society_id')}
                    </div>
                </div>
            </div>

            {/* Cliente */}
            <div className="mb-2 row">
                <label className="col-md-3 col-form-label">Cliente</label>
                <div className="col-md-9">
                    <SelectTwo
                        name="client_id"
                        id="client_id"
                        inputValue={salesInvoice.client_id}
                        options={clientOptions}
                        hasError={fieldHasError('client_id') !== ''}
                        onChange={(option) => {
                            setSalesInvoice({ 
                                ...salesInvoice, 
                                client_id: option?.value || '',
                                payment_statuses_id: [] // Reset deliveries when client changes
                            });
                            onFocusRemove('client_id');
                            if (onClientChange && option?.value) {
                                onClientChange(option.value);
                            }
                            // Limpiar entregas cuando cambie el cliente
                            setAddedDeliveries([]);
                            setSelectedDelivery(null);
                        }}
                        placeholder={loadingClients ? "Cargando clientes..." : "Seleccione un cliente"}
                        onFocus={() => onFocusRemove('client_id')}
                        disabled={!salesInvoice.society_id || loadingClients || is_edit}
                    />
                    <div className="invalid-feedback" style={{ display: 'flex' }}>
                        {fieldErrorMessage('client_id')}
                    </div>
                </div>
            </div>

            {/* Folio (Opcional) */}
            {!is_edit && (
                <div className="mb-2 row">
                    <label className="col-md-3 col-form-label">Folio Factura</label>
                    <div className="col-md-9">
                        <input
                            type="text"
                            className={`form-control ${fieldHasError('folio')}`}
                            name="folio"
                            id="folio"
                            value={salesInvoice.folio || ''}
                            onChange={handleChange}
                            placeholder="Ingrese el folio de la factura"
                            onFocus={() => onFocusRemove('folio')}
                        />
                        <div className="invalid-feedback" style={{ display: 'flex' }}>
                            {fieldErrorMessage('folio')}
                        </div>
                    </div>
                </div>
            )}

            {/* Fecha de Emisión */}
            {!is_edit && (
                <div className="mb-2 row">
                    <label className="col-md-3 col-form-label">Fecha de emisión</label>
                    <div className="col-md-9">
                        <input
                            type="date"
                            className="form-control"
                            name="issue_date"
                            id="issue_date"
                            value={salesInvoice.issue_date || ''}
                            onChange={handleChange}
                            onFocus={() => onFocusRemove('issue_date')}
                        />
                        <div className="invalid-feedback" style={{ display: 'flex' }}>
                            {fieldErrorMessage('issue_date')}
                        </div>
                    </div>
                </div>
            )}

            {/* Selector de Entregas */}
            {loadingDeliveries ? (
                <LazyLoading height={300} />
            ) : (
                <>
                    <label className="col-md-6 col-form-label">Entregas a Facturar</label>
                    <div className="d-flex mb-3 align-items-stretch">
                        <div className="flex-grow-1 me-2">
                            <SelectTwo
                                name="delivery_id"
                                id="delivery_id"
                                inputValue={selectedDelivery ? selectedDelivery.id : null}
                                hasError={fieldHasError('payment_statuses_id') !== ''}
                                options={SelectTwoMapperOptions(deliveryOptions, 'id', ['name'])}
                                onChange={(value: any) => {
                                    const selected = deliveryOptions.find(d => d.id === value.value);
                                    setSelectedDelivery(selected || null);
                                }}
                                onFocus={() => onFocusRemove('payment_statuses_id')}
                                placeholder="Seleccionar Entrega"
                                disabled={!salesInvoice.society_id || !salesInvoice.client_id}
                            />
                            <div className="invalid-feedback" style={{ display: 'flex' }}>
                                {fieldErrorMessage('payment_statuses_id')}
                            </div>
                        </div>

                        <button
                            type="button"
                            className="btn btn-primary"
                            onClick={addDelivery}
                            disabled={!selectedDelivery}
                        >
                            Agregar Entrega
                        </button>
                    </div>
                </>
            )}

            {/* Tabla de Entregas */}
            <div className="table-form">
                <table className="table table-bordered">
                    <thead>
                        <tr>
                            <th>ID Entrega</th>
                            <th>ID Pedido</th>
                            <th>Valor Neto</th>
                            <th>Acciones</th>
                        </tr>
                    </thead>
                    <tbody>
                        {addedDeliveries.map((delivery) => (
                            <tr key={delivery.id}>
                                <td data-label="ID Entrega">#{delivery.id}</td>
                                <td data-label="ID Pedido">#{delivery.sales_order_id ?? '-'}</td>
                                <td data-label="Valor Neto">{formatCurrency(delivery.net_amount || 0)}</td>
                                <td data-label="Acciones">
                                    <ButtonTableAction
                                        callbackFunction={() => removeDelivery(delivery.id)}
                                        classIcon={'mdi mdi-delete'}
                                        colorIcon={'text-danger'}
                                        errorMessage={'No se puede eliminar este registro.'}
                                        title={'Eliminar'}
                                    />
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Total */}
            <div className="total mt-3 d-flex justify-content-end">
                <span className="me-2">Total:</span>
                <span>{formatCurrency(totalAmount)}</span>
            </div>
        </div>
    );
};

export default SalesInvoiceForm;
